import React, { useMemo, useState } from 'react';
import { useId } from 'react-id-generator';
import Carousel from './Carousel';
import { CarouselImageVariants, CarouselProductVariants, CarouselSectionTypes, } from './types';
import { NavigationButton } from '../Navigation';
import { transformDecks, transformSlides } from './utils/transformSlides';
import { Button } from '../Button';
import { ChevronRight } from '../Icons';
import { carouselTiledVariants } from './styles';
import { cn } from '../../lib/utils';
const CarouselTiledCards = ({ className, title, subtitle, text, slides, decks, ctas, ctaSize = 'default', sliderType, productVariant, activeSlide = 0, onSlideChange, }) => {
    const [selectedDeckIndex, setSelectedDeckIndex] = useState(0);
    const [id] = useId(1, 'carousel-');
    const simpleVariant = sliderType === CarouselSectionTypes.Image ? 'images' : 'default';
    const variant = sliderType === CarouselSectionTypes.Image
        ? 'images'
        : sliderType === CarouselSectionTypes.Product &&
            productVariant === CarouselProductVariants.Dark
            ? 'dark'
            : 'light';
    const deckSlides = useMemo(() => {
        if (decks && decks.length > 0) {
            return transformDecks(decks);
        }
        return null;
    }, [decks]);
    const slideElements = useMemo(() => {
        if (!decks || decks.length === 0) {
            return transformSlides(slides || []);
        }
        return deckSlides?.[selectedDeckIndex]?.imageSlides || [];
    }, [slides, decks, deckSlides, selectedDeckIndex]);
    const activeSlideElement = slideElements?.[activeSlide];
    const activeMetadata = activeSlideElement?.props;
    return (React.createElement("div", { className: cn(carouselTiledVariants.outerWrapperVariants({ variant }), className) },
        React.createElement("div", { className: carouselTiledVariants.textContainerVariants({ variant }) },
            React.createElement("span", { className: carouselTiledVariants.subtitleVariants({ variant }) }, subtitle),
            React.createElement("h2", { className: carouselTiledVariants.titleVariants({ variant: simpleVariant }) }, title),
            text && productVariant !== CarouselProductVariants.Dark && (React.createElement("p", { className: carouselTiledVariants.descriptionVariants({ variant: simpleVariant }) }, text))),
        React.createElement("div", { className: carouselTiledVariants.ctaContainerVariants({
                variant: simpleVariant,
                hasCtas: (ctas && ctas.length > 0) || deckSlides?.[selectedDeckIndex]?.cta ? true : false,
                gapSize: ctaSize === 'sm' ? 'sm' : 'default',
            }) },
            ctas?.map((cta, index) => {
                if (React.isValidElement(cta) && cta.type === Button) {
                    return cta;
                }
                const isSecondary = index === 1;
                return (React.createElement(Button, { key: `${index}-"cta"}`, asChild: true, variant: isSecondary ? 'text' : undefined, size: ctaSize, className: isSecondary ? 'underline' : undefined }, cta));
            }),
            deckSlides?.[selectedDeckIndex]?.cta && (React.createElement(Button, { asChild: true }, deckSlides[selectedDeckIndex].cta))),
        React.createElement("div", { className: carouselTiledVariants.navigationContainerVariants({ variant }) },
            React.createElement(NavigationButton, { className: cn(`swiper-nav-prev-${id}`, 'swiper-nav-prev', carouselTiledVariants.navigationButtonVariants({ variant })), direction: "left", variant: "arrow" }),
            React.createElement(NavigationButton, { className: cn(`swiper-nav-next-${id}`, 'swiper-nav-next', carouselTiledVariants.navigationButtonVariants({ variant })), direction: "right", variant: "arrow" })),
        React.createElement("div", { className: carouselTiledVariants.carouselWrapperVariants({ variant }) },
            React.createElement(Carousel, { id: id, onSlideChange: onSlideChange, className: carouselTiledVariants.carouselVariants({ variant, sliderType }), slideElements: slideElements, slidesPerView: "auto", loop: slideElements.length > 2 }),
            deckSlides?.[selectedDeckIndex]?.cta && (React.createElement(Button, { asChild: true, className: "hidden md:flex" }, deckSlides[selectedDeckIndex].cta))),
        sliderType === CarouselSectionTypes.Image && (React.createElement("div", { className: carouselTiledVariants.imageCarouselTextVariants({
                variant: decks && decks.length > 0
                    ? CarouselImageVariants.Sidenav
                    : activeMetadata
                        ? CarouselImageVariants.Simple
                        : null,
            }) }, decks && decks.length > 0
            ? decks.map((deck, index) => (React.createElement("div", { key: deck._id, className: carouselTiledVariants.deckButtonVariants({
                    isSelected: selectedDeckIndex === index,
                }) },
                React.createElement(Button, { variant: "text", onClick: () => setSelectedDeckIndex(index), className: "!text-2xl/[1.25] py-6 border-none w-full flex items-center justify-between" },
                    deck.navTitle,
                    React.createElement(ChevronRight, { className: "size-3", "aria-hidden": "true" })))))
            : activeMetadata && (React.createElement(React.Fragment, null,
                activeMetadata.imageName && (React.createElement("div", { className: "text-2xl xl:text-[2rem] font-bold leading-[1.7]" }, activeMetadata.imageName)),
                activeMetadata.imageTitle && (React.createElement("div", { className: "text-base xl:text-xl uppercase font-medium leading-[1.7]" }, activeMetadata.imageTitle)),
                activeMetadata.imageBio && (React.createElement("div", { className: "mt-2.5 text-base xl:text-xl leading-[1.5]" }, activeMetadata.imageBio)))))),
        React.createElement("div", { className: carouselTiledVariants.carouselBgWrapperVariants({ variant }) },
            React.createElement("div", { className: cn(carouselTiledVariants.carouselBgVariants({ variant })) }))));
};
export default CarouselTiledCards;
